import * as _ from "lodash";
import * as angular from "angular";
import { CheckManager } from "../shared/check-manager";
import { Filter } from "../models/filter.model";
import { Issue } from "../models/issue.model";
import { NavigationBarHelperService } from "../components/navigation/navigation-bar.helper";
import { IssueLogService } from "../services/issue-log.service";
import { IssueService } from "../services/issue.service";
import { fileConstants } from "../utils/constants/files.constants";

export class IssueDataProvider {
    private $q: any;
    private $http: any;
    private service: IssueService;
    private issueLogService: IssueLogService;
    private navigationBarService: NavigationBarHelperService;
    private $timeout: any;
    private __env: any;
    private modelsUtils: any;

    public checkManager: CheckManager;
    public observers = [];
    public allIssuesPromise: any = null;
    public count: number;
    public mailboxLabels = [];
    public filteredIssues: Issue[] = [];
    public issue: Issue;
    public searchPromise: any = null;
    public issueFilter: any = null;
    public infiniteItems: any;
    public statuses: Array<any> = null;
    public origins: Array<any> = null;
    public priorities: Array<any> = null;
    public acceptedFileTypes = [...fileConstants.acceptedFileTypes];

    constructor(
        $q: any,
        $http: any,
        $timeout: any,
        __env: any,
        IssueService: IssueService,
        IssueLogService: IssueLogService,
        NavigationBarHelperService: NavigationBarHelperService,
        ModelsUtils: any
    ) {
        this.$q = $q;
        this.$http = $http;
        this.service = IssueService;
        this.issueLogService = IssueLogService;
        this.navigationBarService = NavigationBarHelperService;
        this.$timeout = $timeout;
        this.checkManager = new CheckManager(this.filteredIssues);
        this.__env = __env;
        this.modelsUtils = ModelsUtils;
        this.issue = this.modelsUtils.issue();

        this.infiniteItems = {
            numLoaded_: 0,
            toLoad_: 0,
            loading: false,
            getItemAtIndex: (index: number) => {
                if (index > this.infiniteItems.numLoaded_ - 1) {
                    this.infiniteItems.fetchMoreItems_(index);
                    return null;
                }

                return this.filteredIssues[index];
            },
            // For infinite scroll behavior, we always return a slightly higher
            // number than the previously loaded items.
            getLength: () => {
                if (
                    angular.isDefined(this.count) &&
                    this.infiniteItems.numLoaded_ + 5 > this.count
                ) {
                    return this.count;
                }
                return this.infiniteItems.numLoaded_ + 5;
            },

            fetchMoreItems_: (index: number) => {
                if (this.infiniteItems.toLoad_ < index) {
                    this.infiniteItems.loading = true;
                    this.infiniteItems.toLoad_ += 20;
                    let offsetValue = null;
                    if (this.filteredIssues && this.filteredIssues.length > 0) {
                        let lastValue =
                            this.filteredIssues[this.filteredIssues.length - 1];
                        offsetValue = {
                            csqid: lastValue.csqid,
                            relevance: lastValue.relevance
                                ? lastValue.relevance
                                : null,
                        };
                    }
                    this.getFilteredIssues(this.issueFilter, offsetValue).then(
                        (issues) => {
                            this.infiniteItems.numLoaded_ = issues.length;
                            this.infiniteItems.loading = false;
                        }
                    );
                }
            },
            reset: () => {
                this.infiniteItems.numLoaded_ = 0;
                this.infiniteItems.toLoad_ = 0;
            },
        };
    }

    thumbnailForLogItem(log: any) {
        if (log.attachments) {
            for (let i = 0; i < log.attachments.length; i++) {
                if (log.attachments[i].thumbnailImage) {
                    return;
                }

                let thumbnailImage = null;
                let extension = log.attachments[i].dctype;
                angular.forEach(this.acceptedFileTypes, (value, index) => {
                    if (
                        value.types &&
                        extension &&
                        value.types.indexOf(extension.toLowerCase()) > -1
                    ) {
                        thumbnailImage = value.imgpath;
                    }
                });
                if (!thumbnailImage) {
                    log.attachments[i].thumbnailImage =
                        "/img/filetypes/none.png";
                }
                log.attachments[i].thumbnailImage = thumbnailImage;
            }
        }
    }

    getFilteredIssues(filter: Filter, lastValue: any): Promise<Issue[]> {
        this.issueFilter = filter;
        this.infiniteItems.loading = true;
        const deferred = this.$q.defer();

        if (this.searchPromise) {
            this.searchPromise.resolve("user cancelled");
        }
        this.searchPromise = this.$q.defer();

        this.$http({
            url: this.__env.apiUrl() + "/query/filter/",
            method: "GET",
            params: {
                filter: filter,
                last: lastValue,
            },
            timeout: this.searchPromise.promise,
        }).then(
            (response) => {
                if (response.data) {
                    const json = response["data"];
                    for (let i = 0; i < json.data.length; i++) {
                        this.filteredIssues.push(
                            this.modelsUtils.issue(json.data[i])
                        );
                    }
                    this.infiniteItems.loading = false;
                    this.setFilteredIssuesCount(parseInt(json.count));
                    this.setMailboxLabels(json.mailbox_counts);
                    if (
                        !this.navigationBarService.smallScreen &&
                        (!this.issue || !this.issue.csqid) &&
                        this.filteredIssues.length
                    ) {
                        this.getIssue(this.filteredIssues[0].csqid);
                    } else {
                        this.issue.loading = false;
                    }
                    deferred.resolve(this.filteredIssues);
                } else {
                    deferred.reject(response);
                }
            },
            //If there already is another request in progress don't stop loading
            (response) => deferred.reject(response)
        );
        return deferred.promise;
    }

    setFilters(filters: Filter): void {
        this.issueFilter = filters;
        this.setFilteredIssuesCount(undefined);
        this.infiniteItems.reset();
        this.resetFilteredIssues();
    }

    resetFilteredIssues(): void {
        this.filteredIssues.splice(0, this.filteredIssues.length);
    }

    resetIssue(): void {
        this.issue.loading = true;
        this.issue.csqid = null;
    }

    reloadData(): void {
        this.setFilteredIssuesCount(undefined);
        this.infiniteItems.reset();
        this.resetFilteredIssues();
    }

    getStatuses(): Promise<any> {
        if (this.statuses !== null) {
            return this.$q.resolve(this.statuses);
        } else {
            const deferred = this.$q.defer();
            this.service.statuses().then(
                (response) => {
                    const json = response["data"];
                    this.statuses = json.data;
                    deferred.resolve(this.statuses);
                },
                (error) => {
                    this.statuses = [];
                    deferred.reject(error);
                }
            );
            return deferred.promise;
        }
    }

    getOrigins(): Promise<any> {
        if (this.origins !== null) {
            return this.$q.resolve(this.origins);
        } else {
            const deferred = this.$q.defer();
            this.service.origins().then(
                (response) => {
                    const json = response["data"];
                    this.origins = json.data;
                    deferred.resolve(this.origins);
                },
                (error) => {
                    this.origins = [];
                    deferred.reject(error);
                }
            );
            return deferred.promise;
        }
    }

    getPriorities(): Promise<any> {
        const deferred = this.$q.defer();
        this.priorities = ["None", "Low", "Medium", "High"];
        deferred.resolve(this.priorities);
        return deferred.promise;
    }

    clearAll(): void {
        this.filteredIssues = [];
        this.issue = this.modelsUtils.issue();
    }

    getFilteredIssuesCount(): number {
        return this.count;
    }

    setFilteredIssuesCount(filteredIssuesCount: number): void {
        this.count = filteredIssuesCount;
        this.checkManager.setTotalCount(this.count);
    }

    setMailboxLabels(labels: Array<any>): void {
        this.mailboxLabels = labels;
    }

    getMailboxLabels(): Array<any> {
        return this.mailboxLabels;
    }

    getIssue(id: number | string): Promise<any> {
        const deferred = this.$q.defer();
        this.issue.loading = true;

        this.service.info({ id: id }).then(
            (response) => {
                const json = response["data"];
                this.issue.loading = false;
                if (json.success) {
                    this.issue.setData(json.data);
                    deferred.resolve(this.issue);
                } else {
                    this.emptyIssue();
                }
                deferred.reject();
            },
            () => deferred.reject()
        );

        return deferred.promise;
    }

    createIssue(issueData: any): Promise<any> {
        const deferred = this.$q.defer();
        let reloaded = false;
        this.service.create(issueData).then(
            (response) => {
                const json = response["data"];
                if (json.success) {
                    this.issue.setData(json.data);
                    this.issue.loading = false;

                    if (
                        this.issueFilter &&
                        this.issueFilter.status != json.data.csqasid &&
                        this.issueFilter.status != 6
                    ) {
                        this.issueFilter.status = "6";
                        this.issue.setData(json.data);
                    } else {
                        if (!this.filteredIssues.length) {
                            reloaded = true;
                            this.reloadData();
                        } else {
                            if (
                                angular.isDefined(this.filteredIssues[0]) &&
                                this.filteredIssues[0].csqid !=
                                    json.data.csqid &&
                                (this.filteredIssues.length > 0 ||
                                    !this.infiniteItems.loading)
                            ) {
                                //in case data provider has already pushed onto list
                                this.addIssueToMailboxLabels(json.data);
                                this.filteredIssues.unshift(
                                    this.modelsUtils.issue(json.data)
                                );
                            }

                            this.getIssue(this.issue.csqid);
                        }
                    }
                    if (!reloaded) {
                        this.replaceIssue(this.issue);
                    }
                    deferred.resolve(this.issue);
                } else {
                    deferred.reject(json);
                }
            },
            (error) => deferred.reject(error)
        );
        return deferred.promise;
    }

    addNote(note: any, issue: Issue): Promise<any> {
        const deferred = this.$q.defer();

        let data = _.cloneDeep(note);
        angular.extend(data, issue);

        this.issueLogService.create(data).then(
            (response) => {
                const json = response["data"];
                if (json.success) {
                    this.replaceIssue(this.modelsUtils.issue(json.data));
                    if (json.data.csqid == issue.csqid) {
                        issue.setData(json.data);
                    }
                    deferred.resolve(issue);
                } else {
                    deferred.reject(json);
                }
            },
            (error) => {
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    setPriority(issue: Issue): Promise<any> {
        const deferred = this.$q.defer();

        this.issueLogService.priority(issue).then(
            (response) => {
                const json = response["data"];
                if (json.success) {
                    this.replaceIssue(this.modelsUtils.issue(json.data));
                    issue.setData(json.data);
                    deferred.resolve(issue);
                }
            },
            (error) => {
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    setStatus(issue: Issue): Promise<any> {
        const deferred = this.$q.defer();

        this.issueLogService.status(issue).then(
            (response) => {
                const json = response["data"];
                if (json.success) {
                    this.replaceIssue(this.modelsUtils.issue(json.data));
                    issue.setData(json.data);
                    this.setMailboxLabels(json.mailbox_counts);
                    deferred.resolve(issue);
                }
            },
            (error) => {
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    setAssigned(issue: Issue): Promise<any> {
        const deferred = this.$q.defer();

        this.issueLogService.assign(issue).then(
            (response) => {
                const json = response["data"];
                if (json.success) {
                    issue.setData(json.data);
                    deferred.resolve(issue);
                }
            },
            (error) => {
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    spliceIssue(issue: Issue, changedStatus: boolean = false): void {
        //Remove issue from list
        if (this.filteredIssues !== null) {
            let issueIndex = this.filteredIssues
                .map((el) => {
                    return el.csqid;
                })
                .indexOf(issue.csqid);

            if (issueIndex > -1) {
                this.deleteIssueFromMailboxLabelsAndCounts(
                    issue,
                    changedStatus
                );
                this.filteredIssues.splice(issueIndex, 1);

                if ((this.issue.csqid = issue.csqid)) {
                    if (this.filteredIssues.length) {
                        this.getIssue(this.filteredIssues[0].csqid);
                    } else {
                        this.emptyIssue();
                    }
                }
            }
            this.notifyObservers();
        }
    }

    emptyIssue(): void {
        this.issue.logs = [];
        this.issue.houseoccupant = this.modelsUtils.houseOccupant();
        this.issue.assigned = this.modelsUtils.user();
        this.issue.status = {};
        this.issue.house = this.modelsUtils.house();
        this.issue.csqid = null;
        this.issue.csqdetails = null;
        this.issue.loading = false;
    }

    deleteIssue(issue: Issue): Promise<any> {
        const deferred = this.$q.defer();
        this.service.delete({ id: issue.csqid }).then((response) => {
            const json = response["data"];
            if (json.success) {
                this.spliceIssue(issue);
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    replaceIssue(issue: Issue): void {
        this.$timeout(() => {
            let existingIssue = this.issueWithID(issue.csqid);
            if (existingIssue) {
                existingIssue.setData(issue);
            } else {
                if (
                    this.issueFilter.status == 6 ||
                    this.issueFilter.status == issue.csqasid
                ) {
                    this.filteredIssues.unshift(issue);
                }
            }
            this.notifyObservers();
        });
    }

    processSocketUpdate(socketData): void {
        // if(filteredIssues) {
        //     if(socketData.csqid == issue.csqid){
        //         issue.setData(socketData);
        //         if(socketData.csqasid == issue.csqasid){
        //             this.replaceIssue(new Issue(socketData));
        //         } else{
        //             this.getIssue(filteredIssues[0]);
        //         }
        //     } else {
        //         this.replaceIssue(new Issue(socketData));
        //     }
        // }
    }

    processSocketCreate(socketData): void {
        // if(filteredIssues && (filteredIssues.length > 0 || !dataProvider.infiniteItems.loading)) {
        //     if (!filteredIssues.length) {
        //         dataProvider.reloadData();
        //     } else{
        //         if (angular.isDefined(filteredIssues[0]) && filteredIssues[0].csqid != socketData.csqid && (filteredIssues.length > 0 || !dataProvider.infiniteItems.loading)){
        //             this.addIssueToMailboxLabels(socketData);
        //         }
        //         this.replaceIssue(new Issue(socketData));
        //     }
        // }
    }

    addIssueToMailboxLabels(issue: Issue): void {
        if (!this.mailboxLabels[6]) {
            this.mailboxLabels[6] = 0;
        }
        this.mailboxLabels[6] = parseInt(this.mailboxLabels[6]) + 1;
        if (issue) {
            if (!this.mailboxLabels[issue.csqasid]) {
                this.mailboxLabels[issue.csqasid] = 0;
            }
            this.mailboxLabels[issue.csqasid] =
                parseInt(this.mailboxLabels[issue.csqasid]) + 1;
            this.infiniteItems.numLoaded_ += 1;
            this.count += 1;
        }
    }

    deleteIssueFromMailboxLabelsAndCounts(
        issue: Issue,
        changedStatus: boolean = false
    ): void {
        this.infiniteItems.numLoaded_ -= 1;
        this.count -= 1;

        if (changedStatus) {
            return;
        }

        this.mailboxLabels[6] -= 1;
        if (issue) {
            this.mailboxLabels[issue.csqasid] -= 1;
        }
    }

    processSocketDelete(socketData): void {
        // if(filteredIssues) {
        //     let existingIssue = this.issueWithID(socketData.csqid);
        //     if(existingIssue){
        //         if(this.issue.csqid == existingIssue.csqid){
        //             if(filteredIssues.length){
        //                 this.getIssue(filteredIssues[0]);
        //             }
        //         }
        //         this.spliceIssue(existingIssue);
        //     }
        // }
    }

    issueWithID(id: string | number): Issue {
        for (let i = 0; i < this.filteredIssues.length; i++) {
            if (this.filteredIssues[i].csqid == id) {
                return this.filteredIssues[i];
            }
        }
        return null;
    }

    addObserver(callback: any): void {
        this.observers.push(callback);
    }

    notifyObservers(): void {
        for (let i = 0; i < this.observers.length; i++) {
            this.observers[i]();
        }
    }
}

angular
    .module("vcDataProvider")
    .service("IssueDataProvider", IssueDataProvider);
